<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePremiumStore } from '@state/stores/premium'
import DataLayer from '@services/data-layer/index'
import { centsToDollars } from './../../../utils/format-price'
import Logo from './../../../components/logo'
import formatDate from './../../../utils/format-date-full'
import { trialDays } from '../../../configuration.js'
import { useAnalytics } from '@composables/use-analytics'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import { billingPeriodFromDurationMonths } from '@services/mixpanel/analytics-spec'
import premiumFunnelOrigin from '@utils/premium-funnel-origin'

const subscriptionService = DataLayer.subscriptions
const route = useRoute()
const router = useRouter()
const premiumStore = usePremiumStore()
const analyticsService = useAnalytics()

const paymentMethods = [
    {
        alias: 'stripe',
        title: 'Credit Card',
        description: 'Visa, Mastercard & AmEx',
        icon: 'credit-card',
    },
    {
        alias: 'paypal',
        title: 'PayPal',
        description: '',
        icon: 'paypal',
    },
]
const selectedMethod = ref('')

const currentSubscription = computed(() => premiumStore.currentSubscription)
const selectedPlan = computed(() => premiumStore.selectedPlan)
const paymentMethod = computed(() => premiumStore.paymentMethod)
const isPremiumUser = computed(() => premiumStore.isPremium)
const entitledFreeTrial = computed(
    () => !premiumStore.hasEverBeenPremium && selectedPlan.value?.durationMonths === 1
)
const hasActivePlan = computed(
    () => currentSubscription.value && new Date(currentSubscription.value.endTime) > new Date()
)
const filteredPaymentMethods = computed(() => {
    if (!hasActivePlan.value || paymentMethod.value === 'manual') return paymentMethods
    return paymentMethods.filter(method => method.alias === paymentMethod.value)
})
const nextBillingDateValue = computed(() => {
    if (!selectedPlan.value?.durationMonths) return
    const date = new Date()
    const nextBillingDate = entitledFreeTrial.value
        ? date.setDate(date.getDate() + trialDays)
        : date.setMonth(date.getMonth() + selectedPlan.value.durationMonths)
    return formatDate(new Date(nextBillingDate))
})

watch(
    isPremiumUser,
    isPremium => {
        if (isPremium && route.query.postAuth) {
            router.replace(premiumFunnelOrigin.consume() || { name: 'home' })
        }
    },
    { immediate: true }
)

function redirect(url) {
    window.location = url
}

async function proceedCheckout(method) {
    analyticsService.premiumUpsellPaymentSelected(route.params.planId, method.alias)
    selectedMethod.value = method
    const checkoutData = {
        subscriptionsProductsId: selectedPlan.value.id,
        paymentProcessor: selectedMethod.value.alias,
    }
    const res = await subscriptionService.checkout(checkoutData)

    paywallTracker.onPurchaseStarted({
        productId: selectedPlan.value.id,
        billingPeriod: billingPeriodFromDurationMonths(selectedPlan.value.durationMonths),
        isTrial: entitledFreeTrial.value,
        isReturning: premiumStore.hasEverBeenPremium,
    })

    redirect(res.body.redirectUrl)
}

onMounted(() => {
    premiumStore.setSelectedPlanId(route.params.planId)
})
</script>

<template>
    <div class="select-payment">
        <BaseContent>
            <BaseSection padded>
                <div class="logo">
                    <Logo
                        :is-linked-to-home="false"
                        size="m"
                    />
                </div>
            </BaseSection>
            <BaseSection
                v-if="selectedPlan"
                padded
            >
                <div class="selected-plan">
                    <h2 class="selected-plan-header">Your plan</h2>
                    <p class="selected-plan-title">{{ selectedPlan.title }}</p>
                    <hr class="separator" >
                    <div class="price">
                        <span>{{ entitledFreeTrial ? 'Pay today' : 'Price' }}</span>
                        <span>{{ centsToDollars(entitledFreeTrial ? 0 : selectedPlan.price) }}</span>
                    </div>
                    <div class="next-billing-date">
                        <span>Next billing day</span>
                        <span>{{ nextBillingDateValue }}</span>
                    </div>
                    <div class="change-plan-container">
                        <router-link
                            class="change-plan"
                            :to="{ name: 'premium/view-plans', query: { ...route.query } }"
                        >Change Plan</router-link>
                    </div>
                </div>
            </BaseSection>

            <BaseSection padded>
                <h1 class="main-heading">Payment Method</h1>
                <div class="methods">
                    <div
                        v-for="method of filteredPaymentMethods"
                        :key="'payment-method' + method.alias"
                        class="method"
                        :title="method.title"
                        :aria-label="method.title"
                        @click="proceedCheckout(method)"
                    >
                        <div class="method-details">
                            <div>
                                <p class="method-title">{{ method.title }}</p>
                                <div>
                                    <p
                                        v-if="method.description"
                                        class="method-description"
                                    >
                                        {{ method.description }}
                                    </p>
                                </div>
                            </div>
                            <BaseIcon
                                class="icon icon-payment"
                                :name="method.icon"
                            />
                        </div>
                    </div>
                </div>
            </BaseSection>

            <transition name="fade">
                <BaseSection v-show="selectedMethod" />
            </transition>
        </BaseContent>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.select-payment {
    text-align: center;
}

.logo {
    display: inline-block;
    text-align: left;
}

.selected-plan {
    width: 400px;
    max-width: 100%;
    padding: $s-m;
    margin: 0 auto;
    color: $c-white;
    background: $c-frisky-channel-grad;
    border-radius: 20px;
}

.selected-plan-header {
    @extend %h5;
}

.selected-plan-title {
    color: $c-white;
    text-align: left;
}

.separator {
    opacity: 0.5;
}

.price {
    display: flex;
    justify-content: space-between;
    color: $c-white;

    @extend %p;
}

.next-billing-date {
    display: flex;
    justify-content: space-between;
    color: $c-white;

    @extend %p;
}

.change-plan-container {
    margin-top: $s-m;
    text-align: right;
}

.change-plan {
    color: $c-white;
    opacity: 0.6;

    @extend %contentSubtitleM;
}

.main-heading {
    @include h3;

    margin-top: $s-s;
    margin-bottom: $s-xxl;
    letter-spacing: 0.25px;

    @include desktop {
        @include container;
        @include h1;

        margin-bottom: $s-xxl;
    }

    @include sidePadding;
}

.methods {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.method-details {
    display: flex;
    gap: $s-m;
    align-items: center;
    justify-content: space-between;
    text-align: left;
}

.method-title {
    margin: 0;
    font-weight: normal;

    @extend %contentTitleM;
}

.method-description {
    margin: 0;

    @extend %contentSubtitleM;
}

.method {
    position: relative;
    width: 500px;
    max-width: 100%;
    padding: $s-m $s-xxl;
    cursor: pointer;
    background: $c-black-grey-gradient;
    border-radius: $button-border-radius;

    + .method {
        margin-top: $s-m;
    }

    &:hover {
        background: $c-grey-mine-shaft;
    }
}

.method-button {
    margin-top: $s-xxl;
}

.icon-payment {
    flex-shrink: 0;
    width: 35px;
    height: 35px;
    fill: $c-white;
}

@include desktop {
    .select-payment {
        margin-top: $s-xxl;
    }
}
</style>
